import clsx from 'clsx';
import { ReactNode, useRef, useState } from 'react';
import { useSectionState } from '../sop-workflow/SectionStateContext';
import {
  CARD_ROOT_CLASS,
  CARD_VARIANTS,
  SOPCardId,
  getDetachPosition,
} from './sop-card-utils';
import { CollapsibleCardHeader } from './CollapsibleCardHeader';

interface Props {
  title: ReactNode;
  icon?: ReactNode;
  subtitle?: string;
  titleAfter?: ReactNode;
  headerAction?: ReactNode;
  below?: ReactNode;
  summary?: ReactNode;
  children: ReactNode;
  detachId?: SOPCardId;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  chevronDirection?: 'up' | 'down';
  defaultExpanded?: boolean;
  collapsible?: boolean;
  ruled?: boolean;
  variant?: keyof typeof CARD_VARIANTS;
  padding?: string;
  bodyGap?: string;
}

export function CollapsibleCard(props: Props) {
  const {
    title,
    icon,
    subtitle,
    titleAfter,
    headerAction,
    below,
    summary,
    children,
    detachId,
    collapsed,
    onToggleCollapsed,
    chevronDirection,
    defaultExpanded = true,
    collapsible = true,
    ruled = false,
    variant = 'section',
    padding,
    bodyGap,
  } = props;

  const { sections, toggleSection, updateSectionPosition } = useSectionState();
  const [localExpanded, setLocalExpanded] = useState(defaultExpanded);
  const cardRef = useRef<HTMLDivElement>(null);

  const isControlled = collapsed !== undefined;
  const isCollapsed =
    collapsible && (isControlled ? collapsed : !localExpanded);
  const onToggle = isControlled
    ? onToggleCollapsed
    : () => setLocalExpanded((prev) => !prev);

  const styles = CARD_VARIANTS[variant];
  const headerStyles = ruled
    ? {
        ...styles,
        headerRule:
          'border-b border-onlight-line-3 dark:border-ondark-line-2 pb-3',
      }
    : styles;
  const bodyClass = bodyGap ?? styles.body;
  const label = typeof title === 'string' ? title : 'section';
  const pointsDown = chevronDirection
    ? chevronDirection === 'down'
    : isCollapsed;

  const onDetach = () => {
    if (!detachId) {
      return;
    }
    if (cardRef.current) {
      const alreadyDetached = Object.values(sections).filter(
        (section) => section.isDetached
      ).length;
      updateSectionPosition(
        detachId,
        getDetachPosition(
          cardRef.current.getBoundingClientRect(),
          alreadyDetached
        )
      );
    }
    toggleSection(detachId);
  };

  if (detachId && sections[detachId]?.isDetached) {
    return null;
  }

  return (
    <div
      ref={cardRef}
      className={clsx(CARD_ROOT_CLASS, styles.root, padding ?? styles.padding)}
    >
      <CollapsibleCardHeader
        title={title}
        icon={icon}
        subtitle={subtitle}
        titleAfter={titleAfter}
        headerAction={headerAction}
        styles={headerStyles}
        showRule={!isCollapsed}
        collapsible={collapsible}
        pointsDown={pointsDown}
        onToggle={onToggle}
        onDetach={(detachId && onDetach) || undefined}
        label={label}
      />
      {!isCollapsed && below}
      {summary && <div className={clsx('min-w-0', bodyClass)}>{summary}</div>}
      {!isCollapsed && (
        <div className={clsx('min-w-0', bodyClass)}>{children}</div>
      )}
    </div>
  );
}

export default CollapsibleCard;
